import React, { useState, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, MessageCircle, ChevronDown, ChevronUp, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { INR } from "@/lib/constants";

/**
 * Slide-in drill-down showing every member in a Renewal-Pulse / Expired bucket.
 * Props:
 *   open, onOpenChange — Sheet control
 *   title, subtitle    — header content
 *   members            — array of enriched membership rows
 *   metric             — 'days_left' | 'days_expired' — which numeric column to show
 *   showNudge          — bool, whether to render WhatsApp nudge button
 */
export default function MembersDrilldownDialog({
  open, onOpenChange, title, subtitle, members = [], metric = "days_left", showNudge = false,
}) {
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState(metric);
  const [sortDir, setSortDir] = useState("asc"); // asc for days_left/expired (smallest = most urgent)

  const filtered = useMemo(() => {
    let list = members;
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter((m) =>
        (m.member_name || "").toLowerCase().includes(s) ||
        (m.member_email || "").toLowerCase().includes(s) ||
        (m.member_phone || "").toLowerCase().includes(s) ||
        (m.plan_name || "").toLowerCase().includes(s)
      );
    }
    const asc = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = a[sortBy]; const bv = b[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number") return (av - bv) * asc;
      return String(av).localeCompare(String(bv)) * asc;
    });
  }, [members, q, sortBy, sortDir]);

  const nudge = useMutation({
    mutationFn: async (m) => (await apiClient.post(`/members/${m.member_id}/whatsapp/renewal-offer`, {})).data,
    onSuccess: (d) => toast.success(d.mocked ? `Renewal offer queued (MOCKED) → ${d.phone}` : `Sent to ${d.sent_to}`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };
  const SortHead = ({ col, children, className = "" }) => (
    <th className={`text-left py-2 px-2 font-semibold cursor-pointer select-none ${className}`} onClick={() => toggleSort(col)}>
      <div className="flex items-center gap-1">
        {children}
        {sortBy === col && (sortDir === "asc" ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
      </div>
    </th>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl bg-zinc-950 border-zinc-800 overflow-y-auto scrollbar-thin" data-testid="drilldown-sheet">
        <SheetHeader>
          <SheetTitle className="font-display uppercase tracking-tight text-2xl">{title}</SheetTitle>
          <SheetDescription className="text-zinc-500 text-xs uppercase tracking-[0.16em]">{subtitle}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="drilldown-search"
            placeholder="Search by name, email, phone, or plan…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-zinc-900 border-zinc-800 pl-9 rounded-sm"
          />
        </div>

        <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
          {filtered.length} of {members.length}
        </div>

        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm" data-testid="drilldown-table">
            <thead className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 sticky top-0 bg-zinc-950">
              <tr className="border-b border-zinc-900">
                <SortHead col="member_name">Name</SortHead>
                <SortHead col="plan_name">Plan</SortHead>
                <SortHead col="expiry_date">Expiry</SortHead>
                <SortHead col={metric}>{metric === "days_expired" ? "Days Ago" : "Days Left"}</SortHead>
                {showNudge && <th className="text-right py-2 px-2 font-semibold">Nudge</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => {
                const val = m[metric];
                const urgent = val != null && val <= 7;
                const color = urgent ? "#FF3B30" : val <= 15 ? "#FFEA00" : "#7DD3FC";
                return (
                  <tr key={m.id} className="border-b border-zinc-900 hover:bg-zinc-900/50" data-testid={`drilldown-row-${m.id}`}>
                    <td className="py-3 px-2">
                      <div className="font-semibold truncate max-w-[210px]">{m.member_name || m.member_id}</div>
                      <div className="text-[10px] text-zinc-500 flex items-center gap-2">
                        {m.member_phone && <span className="flex items-center gap-1"><Phone size={9} />{m.member_phone}</span>}
                        {m.member_email && <span className="flex items-center gap-1 truncate max-w-[140px]"><Mail size={9} />{m.member_email}</span>}
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="text-xs truncate max-w-[180px]">{m.plan_name}</div>
                      {m.price ? <div className="text-[10px] text-zinc-500">{INR(m.price)}</div> : null}
                    </td>
                    <td className="py-3 px-2 text-zinc-400 whitespace-nowrap">
                      {m.expiry_date ? new Date(m.expiry_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—"}
                    </td>
                    <td className="py-3 px-2">
                      <Badge className="rounded-sm text-[10px] uppercase tracking-[0.16em] font-bold" style={{ background: `${color}22`, color, border: "none" }}>
                        {metric === "days_expired" ? `${val}d ago` : `${val}d`}
                      </Badge>
                    </td>
                    {showNudge && (
                      <td className="py-3 px-2 text-right">
                        <Button
                          size="sm"
                          data-testid={`nudge-${m.member_id}`}
                          disabled={nudge.isPending || !m.member_phone}
                          onClick={() => nudge.mutate(m)}
                          className="bg-volt hover:bg-volt-hover text-black rounded-sm text-[10px] uppercase tracking-[0.16em] h-7"
                        >
                          <MessageCircle size={12} className="mr-1" /> WhatsApp
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={showNudge ? 5 : 4} className="py-8 text-center text-zinc-600 text-sm">No members match</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </SheetContent>
    </Sheet>
  );
}
